import { RecipientType } from "encamp-shared/src/constants/submissions";
import {
  ReviewMode,
  SignatureType,
  SubmissionMethod,
  SubmissionType,
} from "generated-graphql/graphql";
import { useCreateSubmission } from "hooks/useSubmissionMutations";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import { useCallback } from "react";
import { toUtcMidnightIsoFromDateLike } from "util/dates";

export type CreateStepperSubmissionArgs = {
  submissionType: SubmissionType;
  submissionMethod: SubmissionMethod;
  facilityId: string;
  title: string;
  dueDate: DateTime;
};

/**
 * Creates a stepper (data-entry) submission directly from the Create Submission
 * dialog — document-less, with `initializeStepper` so the backend seeds an
 * OVERVIEW step (making it `usesStepper`, so it always loads in the stepper).
 *
 * This is the only stepper-creation path: the legacy upload flow has no stepper
 * affordance. Reviewers/signature are set later in the review step, so creation
 * uses minimal defaults.
 */
export function useCreateStepperSubmission() {
  const { tenantId } = useTenant();
  const [createSubmission, { loading }] = useCreateSubmission();

  const createStepperSubmission = useCallback(
    async (args: CreateStepperSubmissionArgs): Promise<string | null> => {
      if (!tenantId) {
        throw new Error("Tenant is required to create a submission");
      }

      const result = await createSubmission({
        variables: {
          input: {
            virtualEventId: "",
            tenantId,
            title: args.title,
            submissionType: args.submissionType,
            submissionMethod: args.submissionMethod,
            recipient: RecipientType.REGULATORY_AGENCY,
            notes: "",
            dueDate: toUtcMidnightIsoFromDateLike(args.dueDate) ?? undefined,
            // Review defaults ON: the Review step's "Include reviewers" is the
            // default state, and reviewRequired=false only ever means staff
            // explicitly confirmed the no-review opt-out there.
            reviewRequired: true,
            peerReviewRequired: false,
            reviewMode: ReviewMode.Parallel,
            signatureType: SignatureType.None,
            reviewers: [],
            facilityIds: [args.facilityId],
            comments: [],
            moveToReview: false,
            // The stepper is the data-entry alternative to uploading a filing.
            initializeStepper: true,
          },
        },
      });

      return result.data?.createSubmission?.id ?? null;
    },
    [tenantId, createSubmission]
  );

  return { createStepperSubmission, loading };
}
